import React, { useMemo } from "react";
import { Skeleton } from "./LoadingStates";

// ─── Shared dashboard presentational widgets ──────────────────────────────
// Used by both the Dashboard (Overview) and Money→Fees pages so each metric
// renders identically in one place. Callers pass pre-formatted strings/points.

export function fmt(n) {
  if (n == null) return "—";
  if (typeof n === "string") return n;
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

export function fmtUSD(n) {
  if (n == null) return "—";
  return "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Category -> marker colour for chart annotations. Literals, not CSS vars:
// --bl-warn is referenced around the app but never actually defined.
const ANNOTATION_COLOR = {
  content: "#5758fc",
  kol:     "#f3af4c",
  space:   "#2BD56F",
  release: "#57c7ff",
  paid:    "#ff5d6c",
  other:   "#9aa4bf",
};

/**
 * Tiny dependency-free line chart.
 *
 * `annotations` (optional) marks events on the x-axis:
 *   [{ x: 0..1, label, category }]
 * The caller computes `x` as a fraction of the series width, because only it
 * knows how its buckets map to dates.
 *
 * Two things about this SVG constrain how markers are drawn, and both have bitten
 * before: the viewBox is a fixed 600 wide with preserveAspectRatio="none", so it
 * is stretched horizontally by an arbitrary factor. That means (a) strokes need
 * vectorEffect="non-scaling-stroke" or they render as smeared wedges, and (b)
 * SVG <text> would be horizontally distorted — labels are HTML overlay divs
 * positioned by percentage instead.
 */
export function MiniLineChart({ points, color = "var(--bl-accent)", height = 140, annotations = [] }) {
  // The gradient id must be unique per instance. It used to be the literal
  // "bl-chart-fill" on every chart, and duplicate SVG ids resolve
  // first-definition-wins — so every chart on a page painted the FIRST chart's
  // colour regardless of its own `color` prop.
  const gradientId = useMemo(
    () => `bl-chart-fill-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );
  if (!points || points.length === 0) return <div style={{ height }} />;
  const w = 600;
  const h = height;
  const max = Math.max(...points.map((p) => p.y), 1);
  const min = Math.min(...points.map((p) => p.y), 0);
  const range = max - min || 1;
  const step = w / Math.max(points.length - 1, 1);
  const coords = points.map((p, i) => {
    const x = i * step;
    const y = h - ((p.y - min) / range) * (h - 24) - 12;
    return { x, y, label: p.label, value: p.y };
  });
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const fill = `${path} L${w},${h} L0,${h} Z`;

  // Clamp into [0,1] so an event in the current partial week cannot overflow the card.
  const marks = (annotations || [])
    .filter((a) => a && Number.isFinite(a.x))
    .map((a) => ({ ...a, x: Math.min(1, Math.max(0, a.x)) }));

  const chart = (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height, display: "block" }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#${gradientId})`} />
      {marks.map((a, i) => (
        <line
          key={`ann-${i}`}
          x1={a.x * w} x2={a.x * w} y1={0} y2={h}
          stroke={ANNOTATION_COLOR[a.category] || ANNOTATION_COLOR.other}
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
          opacity="0.75"
        />
      ))}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r="2.5" fill={color}>
          <title>{`${c.label}: ${c.value}`}</title>
        </circle>
      ))}
    </svg>
  );

  if (marks.length === 0) return chart;

  return (
    <div style={{ position: "relative" }}>
      {chart}
      {marks.map((a, i) => (
        <div
          key={`annlbl-${i}`}
          title={a.label}
          style={{
            position: "absolute",
            top: 2,
            left: `${a.x * 100}%`,
            transform: a.x > 0.75 ? "translateX(-100%)" : "translateX(2px)",
            fontSize: 9,
            lineHeight: 1.2,
            maxWidth: 96,
            padding: "1px 4px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "none",
            color: ANNOTATION_COLOR[a.category] || ANNOTATION_COLOR.other,
            background: "rgba(0,0,0,0.35)",
          }}
        >
          {a.label}
        </div>
      ))}
    </div>
  );
}

export function Donut({ slices }) {
  const total = slices.reduce((a, s) => a + s.value, 0) || 1;
  const r = 60, cx = 80, cy = 80;
  let start = -Math.PI / 2;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", flexWrap: "wrap" }}>
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="22" />
        {slices.map((s, i) => {
          const frac = s.value / total;
          const end = start + frac * Math.PI * 2;
          const x1 = cx + r * Math.cos(start);
          const y1 = cy + r * Math.sin(start);
          const x2 = cx + r * Math.cos(end);
          const y2 = cy + r * Math.sin(end);
          const large = end - start > Math.PI ? 1 : 0;
          const d = `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`;
          start = end;
          return <path key={i} d={d} fill="none" stroke={s.color} strokeWidth="22" strokeLinecap="butt" />;
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontFamily="var(--bl-font-numeric)" fill="#fff">{total}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="var(--bl-text-soft)" letterSpacing="0.06em">TOTAL</text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, minWidth: 160 }}>
        {slices.map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, background: s.color, borderRadius: 2 }} />
              <span style={{ color: "var(--bl-text-strong)" }}>{s.label}</span>
            </span>
            <span style={{ color: "var(--bl-text-soft)", fontVariantNumeric: "tabular-nums" }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function KpiCard({ label, value, sub, icon, accent = "var(--bl-accent)", accentRgb: accentRgbProp, loading }) {
  // Callers may pass an explicit accentRgb (e.g. a warning red) to colour the
  // icon chip; otherwise derive it from the accent keyword (success→green).
  const accentRgb = accentRgbProp || (accent.includes("success") ? "0,205,82" : "87,88,252");
  return (
    <div className="bl-glass" style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: `rgba(${accentRgb}, 0.16)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <i className={icon} style={{ color: accent, fontSize: 20 }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        {loading ? (
          <div style={{ marginTop: 6 }}><Skeleton height={24} width="60%" /></div>
        ) : (
          <div style={{ marginTop: 2, fontSize: "clamp(1.4rem, 3.5vw, 1.75rem)", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </div>
        )}
        {sub && !loading && <div style={{ marginTop: 2, fontSize: 12, color: "var(--bl-text-soft)" }}>{sub}</div>}
      </div>
    </div>
  );
}
